import { useState } from 'react';
import { ArrowRight, Check, Shield, Clock, Users } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PricingTableInsurance from '@/components/PricingTableInsurance';
import TestimonialSlider from '@/components/TestimonialSlider';
import ProcessSliderInsurance from '@/components/ProcessSliderInsurance';
import TestimonialFAQSection from '@/components/TestimonialFAQSection';
import ContactPopup from '@/components/ContactPopup';
import MobileSlider from '@/components/MobileSlider';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const TrademarkInsurance = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const benefits = [
    {
      title: 'Litigation Cost Protection',
      description: 'Trademark disputes are expensive and can drag on for months, even if you are in the right.'
    },
    {
      title: 'Nationwide Coverage Options',
      description: 'Standard business policies often don\'t fully cover trademark infringement risks, leaving dangerous gaps.'
    },
    {
      title: 'Reduced Business Risk Exposure',
      description: 'Trademark Insurance helps pay for attorneys, court fees, settlements, and even enforcing your rights against copycats, depending on your policy.'
    }
    
  ];

  const faqs = [
    {
      question: 'What does trademark insurance actually cover?',
      answer: 'Trademark insurance typically covers legal defense costs if you are accused of infringement, and/or enforcement costs if you need to pursue someone infringing on your mark. Policies vary—some cover only defense, others cover both defense and offensive enforcement (often called "abatement" coverage). We help you select the right combination for your risk profile.'
    },
    {
      question: 'Who needs trademark insurance?',
      answer: 'Any business with a registered or pending trademark that represents meaningful brand value should consider coverage—especially companies in competitive industries, e-commerce sellers facing counterfeiting risk, franchisors, and businesses expanding into new markets where conflicts are more likely.'
    },
    {
      question: 'How much does trademark insurance cost?',
      answer: 'Premiums depend on your industry, coverage limits, claim history, and the strength/scope of your trademark portfolio. We work with multiple carriers to find competitive rates tailored to your specific brand risk exposure.'
    },
    {
      question: 'Does trademark insurance cover the cost of registration?',
      answer: 'No. Trademark insurance covers legal costs related to disputes—defense against infringement claims and/or enforcement against infringers. Registration and prosecution costs are separate from insurance and are handled through our trademark registration services.'
    },
    {
      question: 'What is the difference between defense coverage and enforcement coverage?',
      answer: 'Defense coverage pays your legal costs if someone sues you for infringement. Enforcement (abatement) coverage funds your legal costs if you need to sue someone else for infringing your mark. Comprehensive policies include both, giving you protection on offense and defense.'
    },
    {
      question: 'Can I get trademark insurance for a pending application?',
      answer: 'Yes, many carriers offer coverage for marks with pending applications, though terms and premiums may differ from those for already-registered marks. We can advise on timing your policy alongside your registration strategy.'
    },
    {
      question: 'How long does it take to get a policy in place?',
      answer: 'Most trademark insurance policies can be bound within 1-2 weeks once underwriting information is submitted, significantly faster than the trademark registration process itself.'
    },
    {
      question: 'Does trademark insurance replace the need for registration?',
      answer: 'No. Insurance manages financial risk from disputes but does not create legal rights. Federal registration remains essential for establishing enforceable, nationwide exclusive rights. Insurance and registration work together as complementary layers of brand protection.'
    },
    {
      question: 'What happens if I have a claim during the policy period?',
      answer: 'You notify your carrier as soon as a dispute arises. Our team assists in coordinating the claim, working with your assigned legal panel, and ensuring the process moves efficiently so your business operations are minimally disrupted.'
    }
  ];

  return (
    <>
      <Header />
      
      {/* Hero Section - Enhanced Professional Design */}
      <section className="relative py-12 md:py-16 overflow-hidden bg-primary-600" data-testid="trademark-insurance-hero-section">
        {/* Enhanced Background Pattern */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(124,58,237,0.1),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(139,92,246,0.1),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
        
        {/* Dynamic Decorative Elements */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary-600/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-primary-500/15 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 w-[400px] h-[400px] bg-primary-500/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-full mx-auto text-center">
            {/* Centered Content */}
            <div className="space-y-6 text-white">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                Are You Ready To Let Your Brand Go? Or Fight for it Without Financial Stress
              </h1>
              <p className="text-lg text-gray-300 leading-relaxed max-w-3xl mx-auto">
                Your investment on your brand, both time and money is one lawsuit away from wiping your budget. We have a solution for you. Get IP insurance to cover the legal costs so you can fight back without risking your business.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4 justify-center">
                <Button
                  onClick={() => setIsPopupOpen(true)}
                  className="bg-secondary text-white px-8 py-3 rounded-full text-lg font-semibold hover:bg-white hover:text-primary hover:shadow-2xl transition-all"
                  data-testid="trademark-insurance-hero-get-started-btn"
                >
                  Get IP Insurance Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                 
                  variant="outline"
                  className="js-chat border-2 border-white text-white px-8 py-3 rounded-full text-lg font-semibold hover:bg-white/10 hover:text-white"
                  data-testid="trademark-insurance-hero-chat-now-btn"
                >
                  Chat Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section - Image on Left, Content on Right */}
      <section className="py-20 bg-white" data-testid="trademark-insurance-description-section">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Left Side - Image */}
            <div className="order-first md:order-1 relative min-h-[250px] md:min-h-[400px] rounded-3xl shadow-2xl overflow-hidden">
              <img
                src="/assets/img/why-tm-insurance.webp"
                alt="Trademark Insurance"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
            {/* Right Side - Content */}
            <div className="order-last md:order-2 flex flex-col justify-center">
              <h2 className="text-4xl font-bold mb-8">What Trademark Insurance Can Cover</h2>
              <div className="text-lg text-gray-700 leading-relaxed space-y-4">
                <ul className="list-disc leading-relaxed ml-4 text-base">
                  <li className="mb-2">Defend your brand: Legal costs if someone accuses you of infringing their trademark, including attorney fees and court costs (policy-dependent).</li>
                  <li className="mb-2">Enforce your rights: Costs of taking action against competitors who misuse or copy your trademark.</li>
                  <li className="mb-2">Protect cash flow: Litigation can drain your finances; insurance helps you protect cash reserves and keep operating normally.</li>
                  <li className="mb-0">Reassure investors & partners: Coverage signals that you are serious about protecting your intellectual property and business value.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-transparent" data-testid="trademark-insurance-benefits-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Why Trademark Insurance?</h2>
            <p className="text-xl text-gray-600">Trademark disputes are expensive, draining, and can drag on for months—even when you're in the right. Standard business insurance often leaves dangerous gaps in trademark coverage, so you're forced to pay for attorneys, court fees, and settlements out of pocket. Trademark Insurance bridges that gap: it helps cover the legal costs of defending your brand and enforcing your rights, protecting your cash flow while you fight back. Stop gambling with your brand's future—get coverage that turns a potential business-ending lawsuit into a manageable, planned expense.</p>
          </div>

          <MobileSlider>
            {benefits.map((benefit, idx) => (
              <div
                key={idx}
                className="bg-white p-8 rounded-2xl card-hover h-full"
                data-testid={`trademark-insurance-benefit-${idx}`}
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Check className="w-8 h-8" style={{ color: '#ff4d1b' }} />
                  </div>
                  <div>
                    {/* <h3 className="text-xl font-bold mb-2">{benefit.title}</h3> */}
                    <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </MobileSlider>
        </div>
      </section>

      

      {/* Pricing Table */}
      <PricingTableInsurance />

      {/* Process Slider */}
      <ProcessSliderInsurance />

      {/* Testimonials & FAQs Combined */}
      <TestimonialFAQSection faqs={faqs} />

      {/* CTA Section - Strong Call to Action with Background */}
      {/* <section className="relative py-24 overflow-hidden" data-testid="trademark-insurance-cta-section"> */}
        {/* Background Image with Overlay */}
        {/* <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1920&q=80" 
            alt="Professional business team"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-primary-600/95"></div>
          <div className="absolute inset-0 bg-primary-600/20"></div>
        </div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="inline-block mb-6">
              <span className="bg-secondary-600 text-white px-6 py-2 rounded-full text-sm font-semibold uppercase tracking-wide">
                Limited Time Offer
              </span>
            </div>
            
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Don't Let Litigation Costs Threaten Your Brand
            </h2>
            
            <p className="text-xl md:text-2xl text-gray-200 mb-4 leading-relaxed">
              A single infringement dispute can cost more than years of insurance premiums combined.
            </p>
            
            <p className="text-lg text-gray-300 mb-10 max-w-3xl mx-auto">
              Join businesses who protect their brand equity with comprehensive trademark insurance. Get expert 
              guidance, competitive carrier matching, and peace of mind knowing your intellectual property is 
              financially secured.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button
                onClick={() => setIsPopupOpen(true)}
                className="bg-secondary text-white px-10 py-6 rounded-full text-lg font-bold hover:bg-white hover:text-black hover:shadow-2xl hover:scale-105 transition-all"
                data-testid="trademark-insurance-cta-register-btn"
              >
                Get Insured Today
                <ArrowRight className="ml-2 w-5 h-5" style={{ color: '#ff4d1b' }} />
              </Button>
              <Button
               
                variant="outline"
                className="js-chat border-2 border-white text-white px-10 py-6 rounded-full text-lg font-bold hover:bg-white hover:text-primary-600 transition-all"
                data-testid="trademark-insurance-cta-consultation-btn"
              >
                Get Free Consultation
              </Button>
            </div>

            <div className="flex flex-wrap justify-center gap-8 text-white/90">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5" style={{ color: '#ff4d1b' }} />
                <span className="text-sm">Comprehensive Coverage</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" style={{ color: '#ff4d1b' }} />
                <span className="text-sm">Fast Policy Placement</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5" style={{ color: '#ff4d1b' }} />
                <span className="text-sm">Trusted Carrier Network</span>
              </div>
            </div>
          </div>
        </div> */}
      {/* </section> */}

      <Footer />
      <ContactPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} redirectUrl="/ins-application-submitted" />
    </>
  );
};

export default TrademarkInsurance;
